import { createTheme } from "@mui/material/styles";

const INPUT_RADIUS = {
  small: 32,
  medium: 44,
  large: 64,
};

// Base theme used for ALL non-dashboard routes (/, /client, landing pages)
export const baseTheme = createTheme({
  palette: {
    primary: {
      main: "#472ed1",
      light: "#66E0DD",
      dark: "#3a25b0",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#db3a3e",
      light: "#ff6f61",
      dark: "#a5001e",
      contrastText: "#FFFFFF",
    },
    background: {
      default: "#FFFFFF",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#1C1B1F",
      secondary: "#666",
    },
    success: {
      main: "#198754",
    },
    error: {
      main: "#dc3545",
    },
    warning: {
      main: "#ffc107",
    },
    info: {
      main: "#0dcaf0",
    },
  },
  typography: {
    fontFamily: "'Poppins', 'Roboto', 'sans-serif'",
    fontSize: 14,
    h1: { fontSize: "2.5rem", fontWeight: 600 },
    h2: { fontSize: "2rem", fontWeight: 600 },
    h3: { fontSize: "1.75rem", fontWeight: 500 },
    body1: { fontSize: "1rem" },
    body2: { fontSize: "0.875rem" },
  },
  shape: {
    borderRadius: 8,
  },
  spacing: 8,
  components: {
    MuiButton: {
      defaultProps: {
        textTransform: "capitalize",
        size: "small",
      },
      styleOverrides: {
        root: {
          borderRadius: 32,
          textTransform: "none",
          minHeight: "32px",
          padding: "6px 24px",
          fontSize: "0.875rem",
          "&.MuiButton-sizeSmall": {
            minHeight: "24px",
            padding: "4px 16px",
            fontSize: "0.8125rem",
            borderRadius: 16,
          },
          "&.MuiButton-sizeLarge": {
            minHeight: "40px",
            padding: "8px 32px",
            fontSize: "0.9375rem",
            borderRadius: 64,
          },
        },
      },
    },
    MuiLoadingButton: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: {
          borderRadius: 32,
          textTransform: "none",
          minHeight: "32px",
          padding: "7px 24px",
          fontSize: "0.875rem",
          "&.Mui-disabled": {
            opacity: 0.6,
          },
          "&.MuiLoadingButton-loading": {
            opacity: 0.8,
            "& .MuiLoadingButton-loadingIndicator": {
              color: "inherit",
            },
          },
          "&.MuiLoadingButton-sizeSmall": {
            minHeight: "24px",
            padding: "8px 16px",
            fontSize: "0.8125rem",
            borderRadius: 16,
          },
          "&.MuiLoadingButton-sizeLarge": {
            minHeight: "40px",
            padding: "8px 32px",
            fontSize: "0.9375rem",
            borderRadius: 64,
          },
        },
      },
    },

    MuiInput: {
      styleOverrides: {
        root: {
          "&.MuiInputBase-sizeSmall": {
            borderRadius: INPUT_RADIUS.small,
          },
          "&.MuiInputBase-sizeMedium": {
            borderRadius: INPUT_RADIUS.medium,
          },
          "&.MuiInputBase-sizeLarge": {
            borderRadius: INPUT_RADIUS.large,
          },
        },
      },
    },

    MuiInputBase: {
      styleOverrides: {
        root: {
          "&.MuiInputBase-sizeSmall": {
            borderRadius: INPUT_RADIUS.small,
          },
          "&.MuiInputBase-sizeMedium": {
            borderRadius: INPUT_RADIUS.medium,
          },
        },
      },
    },

    MuiTextField: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            "&.MuiInputBase-sizeSmall": {
              borderRadius: INPUT_RADIUS.small,
            },
            "&.MuiInputBase-sizeMedium": {
              borderRadius: INPUT_RADIUS.medium,
            },
            "&.MuiInputBase-sizeLarge": {
              borderRadius: INPUT_RADIUS.large,
            },
          },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "&.MuiInputBase-sizeSmall": {
            borderRadius: INPUT_RADIUS.small,
          },
          "&.MuiInputBase-sizeMedium": {
            borderRadius: INPUT_RADIUS.medium,
          },
          "&.MuiInputBase-sizeLarge": {
            borderRadius: INPUT_RADIUS.large,
          },
        },
      },
    },

    MuiFilledInput: {
      styleOverrides: {
        root: {
          "&.MuiInputBase-sizeSmall": {
            borderRadius: INPUT_RADIUS.small,
          },
          "&.MuiInputBase-sizeMedium": {
            borderRadius: INPUT_RADIUS.medium,
          },
          "&.MuiInputBase-sizeLarge": {
            borderRadius: INPUT_RADIUS.large,
          },
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        root: {
          "&.MuiOutlinedInput-root": {
            "&.MuiInputBase-sizeSmall": {
              borderRadius: INPUT_RADIUS.small,
            },
            "&.MuiInputBase-sizeMedium": {
              borderRadius: INPUT_RADIUS.medium,
            },
            "&.MuiInputBase-sizeLarge": {
              borderRadius: INPUT_RADIUS.large,
            },
          },
        },
      },
    },

    MuiAutocomplete: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            "&.MuiInputBase-sizeSmall": {
              borderRadius: INPUT_RADIUS.small,
            },
            "&.MuiInputBase-sizeMedium": {
              borderRadius: INPUT_RADIUS.medium,
            },
            "&.MuiInputBase-sizeLarge": {
              borderRadius: INPUT_RADIUS.large,
            },
          },
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: "50%",
          width: "32px",
          height: "32px",
          "&.MuiIconButton-sizeSmall": {
            width: "24px",
            height: "24px",
          },
          "&.MuiIconButton-sizeLarge": {
            width: "40px",
            height: "40px",
          },
        },
      },
    },

    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
  },
});
